import React, { ChangeEvent, FocusEvent, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Typography,
  FormControl,
  TextField,
  FormControlLabel,
  Button,
  FormLabel,
  RadioGroup,
  Radio,
  Tooltip,
  IconButton,
} from '@mui/material';
import filesize from "filesize";
import { map as asyncMap } from 'p-iteration';
import { uniqueId, update } from 'lodash'
import { IQuestion } from '../../../models/iquestion';
import { IServiceQuestion } from '../../../models/iserviceQuestion';
import { i18n } from '../../../translate/i18n';
import ServiceOldActionsPanel from '../../../components/ServiceOldActionsPanel/ServiceOldActionsPanel';
import * as actions from '../../../store/actions';
import ActionExpansionPanel from '../../../UI/ActionExpansionPanel/ActionExpansionPanel';
import { IAction } from '../../../models/iaction';
import Upload from '../../../UI/Upload';
import FileList from '../../../UI/FileList';
import { IFile } from '../../../UI/FileList';
import processUpload from '../../../utils/processUpload';
import resizeFile from '../../../utils/imageResizer';
import confirm from '../../../UI/Confirm/confirm';
import api from '../../../axios/api';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { RootState } from '../../../store/types';
import { EUpdateDraftType, IUpdateServiceDraftBody } from '../CreateService/Checklist/Checklist';

type Props = {
  question: IQuestion | IServiceQuestion,
  isNewData: boolean,
  editMode: boolean,
  questionShouldHaveActions: boolean,
  questionRef: React.RefObject<Node>,
  handleOpenNewActionModal: CallableFunction,
  handleOpenEditActionModal: CallableFunction,
  mandatoryActionsForNotComplyQuestions: boolean,
  goToActionPage: (actionId: string) => void,
  createOrUpdateDraft: (body: IUpdateServiceDraftBody) => Promise<void>,
}

const Question: React.FC<Props> = ({
  question,
  isNewData,
  editMode,
  questionShouldHaveActions,
  questionRef,
  handleOpenNewActionModal,
  handleOpenEditActionModal,
  mandatoryActionsForNotComplyQuestions,
  goToActionPage,
  createOrUpdateDraft,
}) => {
  const [comment, setComment] = useState<string>(question.comment);
  const [questionActions, setQuestionActions] = useState(question.actions);
  const [files, setFiles] = useState(question.files);
  const [uploadSubmitError, setUploadSubmitError] = useState<boolean>(false);
  const [displayQuestionDescription, setDisplayQuestionDescription] = useState<boolean>(false);
  const oldActions = useSelector((state: RootState) => state.createService.oldActions);

  const dispatch = useDispatch();

  const updateChanges = async () => {
    if (isNewData) {
      const questionEdited = {
        _id: question._id,
        questionsId: question._id,
        name: question.name,
        groupName: question.groupName as string,
        type: question.type,
        answer: question.answer,
        actions: question.actions.map((action) => {
          return {
            ...action,
            files: action.files.map((file: any) => {
              if (typeof file === "string") {
                return file;
              } else {
                return (file._id || file.id) as string
              }
            }),
          }
        }) as IAction[],
        comment: question.comment,
        commentFlag: question.commentFlag,
        fileFlag: question.fileFlag,
        files: question.files.map((file) => file.id as string || file._id as string),
        weight: question.weight,
        questionHadPendingActions: false,
        description: question.description,
        photo: question.photo?._id,
      }
      questionEdited.questionHadPendingActions = oldActions.some((action) => {
        const questionId = question._id;
        return questionId === action.question;
      }) || question.actions?.length > 0;
      await createOrUpdateDraft({ type: EUpdateDraftType.questions, question: questionEdited });
    }
    
  }

 const handleChangeRadio = async (e: ChangeEvent<HTMLInputElement>, question: IQuestion | IServiceQuestion) => {
    if (mandatoryActionsForNotComplyQuestions && e.target.value === 'NO') {
      handleOpenNewActionModal(question);
    }
    const dispatchCreateServiceChangeQuestionAnswer = async () => dispatch(actions.createServiceChangeQuestionAnswer(e.target.value, question._id));
    if (isNewData){
      await dispatchCreateServiceChangeQuestionAnswer();
      await updateChanges();
    } else dispatch(actions.editServiceChangeQuestionAnswer(e.target.value, question._id));

  }

  const handleChangeComment = async (e: FocusEvent<HTMLInputElement|HTMLTextAreaElement>, questionId: string) => {
    if (isNewData) {
      dispatch(actions.createServiceChangeQuestionComment(e.target.value, questionId));
      await updateChanges();
    } else dispatch(actions.editServiceChangeQuestionComment(e.target.value, questionId));
  }

  const handleUpload = async (files: File[]) => {
    const uploadingFiles = await asyncMap(files, async (file) => {
      const fileOptimized = await resizeFile(file)

      return ({
        file: fileOptimized,
        id: uniqueId(),
        name: fileOptimized.name,
        readableSize: filesize(fileOptimized.size),
        preview: URL.createObjectURL(fileOptimized),
        fileType: fileOptimized.type,
        progress: 0,
        isNotUploaded: true,
        error: false,
        url: null
      } as IFile);
    });

    // props.setUploadSubmitError(false);
    // props.setFileUploadError(false);

    if (isNewData) dispatch(actions.createServiceAddQuestionFile(uploadingFiles, question._id));
    else dispatch(actions.editServiceAddQuestionFile(uploadingFiles, question._id));
    const processUploadPromises = uploadingFiles.map(async (file) => await processUpload(file, updateFile, setUploadSubmitError));
    await Promise.all(processUploadPromises);
  };

  const updateFile = (fileId: string, file: any) => {
    if (isNewData) dispatch(actions.createServiceEditQuestionFile(file, question._id, fileId));
    else dispatch(actions.editServiceEditQuestionFile(file, question._id, fileId));
  };

  const retryUpload = async (file: IFile) => {
    updateFile(file.id as string, {error: false})
    await processUpload(file, updateFile, setUploadSubmitError);
  }

  const handleDeleteQuestionFile = (fileId: string) => {
    if (!editMode && !isNewData) return;
    confirm(i18n.t('handleDeleteDocumentFileFormService.confirm'),
      {
        title: i18n.t('handleDeleteDocumentFileFormService.title'),
        titleColor: 'red',
        disableBackdrop: true
      }).then(
        () => { },
        async () => {
          await handleDeleteFile(fileId, question._id)
        },
      )
  }
  
  const handleDeleteFile = async (fileId: string, questionId: string) => {
    try {
      // props.setUploadSubmitError(false);
      // props.setFileUploadError(false);
      if (fileId.length === 24) await api.delete(`files/${fileId}`)
      if(isNewData) dispatch(actions.createServiceDeleteQuestionFile(questionId, fileId));
      else dispatch(actions.editServiceDeleteQuestionFile(questionId, fileId));
    } catch (e) {
      console.log('e', e);
    }
  }

  return (
    <Box ref={questionRef}>
      <FormControl variant="standard" sx={styles.formControl}>
        <FormLabel
          sx={{ color: (!isNewData && !editMode) ? 'lightgrey' : '#6F8189', ...styles.questionText }}
          component="legend"
        >
          <Box>
            {question.name}
            {
              (question.description || question.photo?.url) &&
              <IconButton sx={styles.infoButton} onClick={() => setDisplayQuestionDescription(!displayQuestionDescription)}>
                <Tooltip title={i18n.t('formControlLabelFormServiceQuestList.questionDescription') as string} sx={styles.infoTooltip} placement="left">
                    <InfoOutlinedIcon fontSize='small'/>
                </Tooltip>
              </IconButton>
            }
          </Box>
          <Button
            variant="outlined"
            disabled={(!isNewData && !editMode)}
            size="small"
            onClick={() => handleOpenNewActionModal(question)}
            color='primary'
            sx={styles.buttonAction}
          >
            {i18n.t('buttonFormservQuesList.text') as string}
          </Button>
        </FormLabel>
        { displayQuestionDescription && <Box sx={styles.questionDescriptionBox}>
          {question.photo && <Box sx={styles.imageBox}><img src={question.photo.url}/></Box>}
          {question.description && <Box
            className="ck-content"
            dangerouslySetInnerHTML={{ __html: question.description}}
            sx={styles.questionDescription}
          />}
        </Box>}
        <RadioGroup aria-label="gender" name={question._id} value={question.answer} onChange={(e) => handleChangeRadio(e, question)} sx={{flexDirection: 'row'}}>
          <FormControlLabel value="YES" control={<Radio disabled={!isNewData && !editMode} />} sx={{fontSize: '0.8rem'}} label={i18n.t('formControlLabelFormServiceQuestList.YES') as string} />
          <FormControlLabel value="NO" control={<Radio disabled={!isNewData && !editMode} />} sx={{fontSize: '0.8rem'}} label={i18n.t('formControlLabelFormServiceQuestList.NO') as string} />
          <FormControlLabel value="NA" control={<Radio disabled={!isNewData && !editMode} />} sx={{fontSize: '0.8rem'}} label={i18n.t('formControlLabelFormServiceQuestList.NA') as string} />
        </RadioGroup>
        {questionShouldHaveActions && <Typography sx={{color: 'red', fontSize: '12px'}}>{i18n.t('formControlLabelFormServiceQuestList.needAction') as string}</Typography>}
        { (question.commentFlag) &&
          <FormControl variant="standard" fullWidth>
            <TextField
              multiline
              rows={3}
              label={i18n.t('textFieldFormServiceQuesList.comment') as string}
              variant='outlined'
              name='comment'
              onChange={ (event) => setComment(event.target.value) }
              onBlur={(event) => handleChangeComment(event, question._id)}
              value={comment}
              margin='normal'
              InputLabelProps={{
                shrink: true,
              }}
              disabled={ (!editMode && !isNewData) }
            />
          </FormControl>
        }
        {
          (question.fileFlag) &&
            <>
              <Upload onUpload={async (files:File[]) => {await handleUpload(files); await updateChanges()}} disabled={!editMode && !isNewData} />
              {question.files?.length > 0 && (
                <FileList
                  files={question.files}
                  onDelete={(fileId: string) => handleDeleteQuestionFile(fileId)}
                  disabled={!editMode && !isNewData}
                  retry={(file: IFile) => retryUpload(file)}
                />
              )}
              {uploadSubmitError && <Box sx={styles.fileError}>{i18n.t('BoxFormService.fileError') as string}</Box>}
            </>
        }
      </FormControl>
      {question.actions && question.actions.length > 0 &&
        <ActionExpansionPanel
          data={question.actions}
          disabled={!isNewData && !editMode}
          onAdd={() => handleOpenNewActionModal(question)}
          onEdit={(action: IAction) => {
            if (isNewData) handleOpenEditActionModal(question, action)
            else goToActionPage(action._id)
          }}
        />
      }
      {(question.oldActions.length > 0) &&
        <ServiceOldActionsPanel
          actions={question.oldActions}
          title={i18n.t('serviceOldActionsPanel.title')}
          showRefreshButton={isNewData}
        />
      }
    </Box>
  );
  
};

const styles = {
  buttonAction: {
    width: "10rem",
    minWidth: "10rem"
  },
  formControl: {
    display: 'flex',
    marginTop: '10px',
    paddingLeft: '24px',
    paddingRight: '24px',
  },
  fileError: {
    color: '#f44336'
  },
  questionText: {
    fontSize: 14,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: "center",
    fontWeight: '500',
  },
  infoButton: {
    padding: 0,
    margin: 0,
    width: '0.1 rem',
  },
  infoTooltip: {
    marginLeft: 1,
    marginRight: 1,
    marginBottom: 0.5
  },
  questionDescriptionBox: {
    marginTop: 1,
    marginBottom: 1,
    flex: 1,
    border: '1px solid lightgrey',
    borderRadius: '12px',
    padding: 2,
    maxWidth: '75%',
  },
  imageBox: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  questionDescription: {
    color: 'grey',
    marginTop: 1,
    marginBottom: 1
  },
}

export default Question;
